import type {
  SidebarChange,
  SidebarEntry,
  SidebarSnapshot,
} from "@/types/sidebar-session";

export function availableSidebarEntries(
  snapshot: SidebarSnapshot,
): SidebarEntry[] {
  return Object.values(snapshot.entries)
    .filter((entry) => {
      const definition = snapshot.registrations[entry.key]?.definition;
      return (
        definition && (definition.isAvailable?.(snapshot.environment) ?? true)
      );
    })
    .sort((left, right) => {
      const a = snapshot.registrations[left.key].definition;
      const b = snapshot.registrations[right.key].definition;
      return a.order - b.order || left.key.localeCompare(right.key);
    });
}

/** Membership and selection are published together, never repaired by a render effect. */
export function applySidebarChange(
  snapshot: SidebarSnapshot,
  change: SidebarChange,
): SidebarSnapshot {
  const entries = { ...snapshot.entries };
  const registrations = { ...snapshot.registrations };
  for (const key of change.remove ?? []) delete entries[key];
  for (const write of change.upsert ?? []) {
    const { definition, entry } = write;
    const registered = registrations[entry.key];
    if (!registered) {
      registrations[entry.key] = {
        definition,
        owners: new Map(),
        implicit: true,
      };
    }
    const availableDefinition = registrations[entry.key].definition;
    if (availableDefinition.isAvailable?.(snapshot.environment) === false) {
      delete entries[entry.key];
    } else {
      entries[entry.key] = entry;
    }
  }
  const next = { ...snapshot, entries, registrations };
  const available = availableSidebarEntries(next);
  const selected =
    change.select === undefined ? snapshot.activeKey : change.select;
  return {
    ...next,
    activeKey: available.some((entry) => entry.key === selected)
      ? selected
      : (available[0]?.key ?? null),
  };
}
